import axios from 'axios';
import { msalInstance, loginRequest } from '../auth/msalConfig';

// Create axios instance with base configuration
const client = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:3001',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor — injects Azure AD ID token as Bearer and adds cache-bust param
client.interceptors.request.use(
  async (config) => {
    // MSAL v5: always use getActiveAccount() — getAllAccounts()[0] can cause
    // AggregateError if the account object doesn't match the internal cache key.
    let account = msalInstance.getActiveAccount();
    if (!account) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        msalInstance.setActiveAccount(account);
      }
    }

    if (account) {
      try {
        const result = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        config.headers.Authorization = `Bearer ${result.idToken}`;
      } catch {
        // acquireTokenSilent failed (MSAL v5 throws AggregateError via Promise.any
        // when all silent strategies fail — e.g. expired tokens, stale cache).
        // Redirect to the login page for a fresh session.
        window.location.replace('/login');
        return new Promise(() => {}); // prevent the request from being sent
      }
    }

    // Add timestamp to prevent caching
    config.params = {
      ...config.params,
      _t: Date.now()
    };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
client.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    console.error('API Error:', error);

    // Redirect to the forbidden page on 403 — user is authenticated but not authorised
    if (error.response?.status === 403) {
      window.location.replace('/forbidden');
      return new Promise(() => {}); // Prevent further error handling
    }

    const errorMessage = error.response?.data?.message || error.message || 'An error occurred';

    return Promise.reject({
      message: errorMessage,
      status: error.response?.status,
      data: error.response?.data
    });
  }
);

export default client;
